import { UserProfile, UserRole } from '../types/auth';

export type OnboardingStepId =
  | 'discover'
  | 'upload'
  | 'record'
  | 'find-artists'
  | 'vote'
  | 'profile'
  | 'contact';

export interface OnboardingStep {
  id: OnboardingStepId;
  title: string;
  description: string;
  /** In-app route; omit for steps that stay on Home. */
  to?: string;
  ctaLabel?: string;
}

const STEPS: Record<OnboardingStepId, OnboardingStep> = {
  discover: {
    id: 'discover',
    title: 'Explore the feed',
    description: 'Browse Beats and Verses on Home. Use filters to find tracks by genre or state.',
  },
  upload: {
    id: 'upload',
    title: 'Upload a beat',
    description: 'Share an instrumental, set a verses limit, and invite rappers to collab.',
    to: '/upload',
    ctaLabel: 'Go to Upload',
  },
  record: {
    id: 'record',
    title: 'Record a verse',
    description:
      'On Home, click a beat card in the feed to open its page. Then use Record on that beat (if verse slots are still available) to drop bars in your browser.',
  },
  'find-artists': {
    id: 'find-artists',
    title: 'Find artists',
    description: 'Search producers and rappers by role or state and visit their profiles.',
    to: '/search',
    ctaLabel: 'Find Artists',
  },
  vote: {
    id: 'vote',
    title: 'Vote on tracks',
    description: 'Support beats and verses you like — your votes help surface what hits.',
  },
  profile: {
    id: 'profile',
    title: 'Check your profile',
    description: 'See your public page, links, and the content you share with the community.',
    ctaLabel: 'Open Profile',
  },
  contact: {
    id: 'contact',
    title: 'Need help?',
    description: 'Questions or issues? Reach support anytime from Contact Us.',
    to: '/contact',
    ctaLabel: 'Contact Us',
  },
};

const ROLE_STEPS: Record<Exclude<UserRole, ''>, OnboardingStepId[]> = {
  producer: ['discover', 'upload', 'find-artists', 'profile', 'contact'],
  rapper: ['discover', 'record', 'find-artists', 'profile', 'contact'],
  producer_rapper: ['discover', 'upload', 'record', 'find-artists', 'profile'],
  listener: ['discover', 'vote', 'find-artists', 'profile', 'contact'],
};

const FALLBACK_STEPS: OnboardingStepId[] = ['discover', 'find-artists', 'profile', 'contact'];

export function getRoleLabel(role: UserRole): string {
  switch (role) {
    case 'producer':
      return 'Producer';
    case 'rapper':
      return 'Rapper';
    case 'producer_rapper':
      return 'Producer & Rapper';
    case 'listener':
      return 'Listener';
    default:
      return 'member';
  }
}

export function getOnboardingSteps(role: UserRole): OnboardingStep[] {
  const ids = role && ROLE_STEPS[role] ? ROLE_STEPS[role] : FALLBACK_STEPS;
  return ids.map(id => {
    const step = STEPS[id];
    if (id === 'profile') {
      return { ...step };
    }
    return step;
  });
}

/** Resolve profile step link once we know the user id. */
export function withProfileLink(steps: OnboardingStep[], userId: string): OnboardingStep[] {
  return steps.map(step =>
    step.id === 'profile' ? { ...step, to: `/profile/${userId}` } : step
  );
}

/**
 * Show onboarding only when the DB column exists and is still null.
 * If the migration is not applied yet, the column is absent → do not show
 * (avoids forcing the tour on every user before Supabase is updated).
 */
export function shouldShowOnboarding(user: UserProfile | null | undefined): boolean {
  if (!user) return false;
  if (!('onboarding_completed_at' in user)) return false;
  return user.onboarding_completed_at == null;
}

export function onboardingLocalStorageKey(userId: string): string {
  return `dhh_onboarding_completed_${userId}`;
}

export function isOnboardingDismissedLocally(userId: string): boolean {
  try {
    return localStorage.getItem(onboardingLocalStorageKey(userId)) === '1';
  } catch {
    return false;
  }
}

export function markOnboardingDismissedLocally(userId: string): void {
  try {
    localStorage.setItem(onboardingLocalStorageKey(userId), '1');
  } catch {
    // Ignore quota / private mode — DB remains source of truth when available.
  }
}
